<template>
  <div class="fuel-wrapper">
    <h2 class="fuel-header">CURRENT FUEL PREDICTIONS</h2>

    <div
      class="market-indicators"
      v-if="brentPrice !== null && exchangeRate !== null"
    >
      <div class="indicator">
        <strong>Brent Crude (USD/bbl):</strong> {{ brentPrice.toFixed(2) }}
      </div>
      <div class="indicator">
        <strong>USD → ZAR:</strong> {{ exchangeRate.toFixed(2) }}
      </div>
    </div>

    <div 
      v-if="fuelData.length" 
      class="cards-mask" 
      @mouseenter="showArrows = true" 
      @mouseleave="showArrows = false"
    >
      <div 
        class="scroll-arrow left-arrow" 
        v-show="showLeftArrow && showArrows" 
        @click="scrollLeft"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </div>
      <div
        ref="scrollContainer"
        class="cards-container"
        v-show="fuelData.length"
        @mouseenter="pauseScroll"
        @mouseleave="resumeScroll"
        @scroll="checkScrollPosition"
      >
        <div
          v-for="(item, idx) in fuelData"
          :key="item.key"
          class="card-wrapper"
        >
          <v-card class="fuel-card">
            <v-card-text class="card-content">
              <div class="card-icon">
                <v-icon class="fuel-icon">mdi-gas-station</v-icon>
              </div>
              <div class="card-text">
                <div class="card-title">{{ item.name }}</div>

                <div class="card-price" v-if="item.predicted === null">
                  {{ formatPrice(item.current) }} ZAR/L
                </div>

                <div class="card-predicted" v-if="item.predicted !== null">
                  <div class="price-direction">
                    <div 
                      :class="getPriceDirectionClass(item.current, item.predicted)"
                      class="trend-indicator"
                    >
                      <v-icon 
                        :class="getPriceDirectionClass(item.current, item.predicted)"
                        class="trend-icon"
                      >
                        {{ getPriceDirectionIcon(item.current, item.predicted) }}
                      </v-icon>
                    </div>
                  </div>
                </div>

                <!-- Recovery block -->
                <div class="recovery" v-if="item.dailyRecoveryAvg !== null || item.monthlyRecoveryAvg !== null">
                  <div class="recovery-item" v-if="item.monthlyRecoveryAvg !== null">
                    <strong>Monthly recovery<span v-if="item.recoverySource === 'approx'"> (approx)</span>:</strong>
                    {{ formatPriceWithoutCurrency(item.monthlyRecoveryAvg) }} ZAR/L
                  </div>
                  <div class="recovery-item" v-if="item.dailyRecoveryAvg !== null">
                    <strong>Daily recovery<span v-if="item.recoverySource === 'approx'"> (approx)</span>:</strong>
                    {{ formatPriceWithoutCurrency(item.dailyRecoveryAvg) }} ZAR/L
                  </div>
                </div>

                <div class="recovery" v-else-if="recoveryTried && !recoveryAvailable">
                  <div class="recovery-item">
                    <em>Recovery data unavailable</em>
                  </div>
                </div>

              </div>
            </v-card-text>
          </v-card>
        </div>
      </div>
      <div 
        class="scroll-arrow right-arrow" 
        v-show="showRightArrow && showArrows" 
        @click="scrollRight"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </div>
    </div>

    <p v-else class="loading">Loading predictions…</p>
  </div>
</template>

<script>
import { CountUp } from 'countup.js';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc
} from "firebase/firestore";
import { firebaseApp } from "../firebaseConfig";

const db = getFirestore(firebaseApp);

/**
 * DAILY & MONTHLY RECOVERY LOGIC
 * --------------------------------
 * 1) Try to load *real* recovery (over/under-recovery in c/l) from RECOVERY_API_URL (your JSON).
 *    Expected per-row fields (we normalize defensively):
 *      { date, product/name ('ULP 93'|'ULP 95'|'D50ppm'), value (cents/L) }
 * 2) If unavailable, we compute an *approximation* from Fuel SA's own price data:
 *      monthlyRecoveryApprox (c/l) = (currentMonthPumpPrice - previousMonthPumpPrice)
 *      dailyRecoveryApprox   (c/l) = monthlyRecoveryApprox / REVIEW_DAYS   (DMRE working rules use ~25 review days)
 *    This is ONLY a proxy (no BFP), but matches the sign/scale generally expected on cards.
 */

export default {
  name: 'FuelPrices',
  data() {
    return {
      API_KEY: '0056013c62a14e77a7011a41588b190b',
      CURRENT_URL: 'https://api.fuelsa.co.za/exapi/fuel/current',
      BYYEAR_URL: 'https://api.fuelsa.co.za/exapi/fuel/byyear',

      // Real recovery API (JSON you control). If unset or fails, we fall back to approximation.
      RECOVERY_API_URL: process.env.VUE_APP_RECOVERY_API_URL || '',

      // External indicators
      EIA_KEY: process.env.VUE_APP_EIA_KEY,
      EIA_URL: 'https://api.eia.gov/v2/seriesid/EPC.RBRTE.D',
      FX_URL: 'https://api.exchangerate.host/latest?base=USD&symbols=ZAR',

      // UI / data
      fuelData: [],
      brentPrice: null,
      exchangeRate: null,
      scrollInterval: null,
      resumeTimeout: null,
      showLeftArrow: false,
      showRightArrow: false,
      showArrows: false,

      // Recovery state
      recoverySeries: [],         // normalized array of { date: Date, productKey: 'ULP 93'|'ULP 95'|'D50ppm', valueCents: number }
      recoveryByProduct: {},      // { 'ULP 93': { latestCents, monthlyAvgCents }, ... }
      recoveryAvailable: false,   // true if we have real recovery values
      recoveryTried: false,       // attempted to fetch recovery?
      // Approximation settings
      REVIEW_DAYS: 25             // DMRE monthly review period (~25 days)
    };
  },
  methods: {
    // --- Formatting helpers ---
    formatPrice(valueCents) {
      return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        minimumFractionDigits: 2
      }).format(valueCents / 100);
    },

    formatPriceWithoutCurrency(valueCents) {
      return (valueCents / 100).toFixed(2);
    },

    // --- Direction helpers ---
    getPriceDirection(current, predicted) {
      if (predicted === null) return 'stable';
      return predicted > current ? 'up' : predicted < current ? 'down' : 'stable';
    },

    getPriceDirectionIcon(current, predicted) {
      const direction = this.getPriceDirection(current, predicted);
      switch (direction) {
        case 'up': 
          return 'mdi-chevron-double-up';
        case 'down': 
          return 'mdi-chevron-double-down';
        default: 
          return 'mdi-minus';
      }
    },

    getPriceDirectionClass(current, predicted) {
      const direction = this.getPriceDirection(current, predicted);
      switch (direction) {
        case 'up': 
          return 'direction-up';
        case 'down': 
          return 'direction-down';
        default: 
          return 'direction-stable';
      }
    },

    // --- FuelSA fetchers ---
    async fetchCurrent() {
      const res = await fetch(this.CURRENT_URL, {
        method: 'GET',
        headers: {
          'key': this.API_KEY,
          'Accept': 'application/json'
        }
      });
      if (!res.ok) throw new Error(`Error fetching current prices: ${res.status}`);
      const data = await res.json();

      return {
        petrol: Array.isArray(data.petrol) ? data.petrol : [],
        diesel: Array.isArray(data.diesel) ? data.diesel : [],
        paraffin: Array.isArray(data.paraffin) ? data.paraffin : []
      };
    },

    async fetchHistory(year) {
      const res = await fetch(`${this.BYYEAR_URL}/${year}`, {
        method: 'GET',
        headers: {
          'key': this.API_KEY,
          'Accept': 'application/json'
        }
      });
      if (!res.ok) {
        throw new Error(`Error fetching history data: ${res.status}`);
      }
      const data = await res.json();

      return {
        petrol: Array.isArray(data.petrol) ? data.petrol : [],
        diesel: Array.isArray(data.diesel) ? data.diesel : [],
        paraffin: Array.isArray(data.paraffin) ? data.paraffin : []
      };
    },

    // Simple naive prediction from history (unchanged)
    getPredicted(historyArr, filterFn) {
      const arr = historyArr.filter(filterFn);
      arr.sort((a, b) => new Date(a.date) - new Date(b.date));
      if (arr.length < 2) return null;
      const last = arr[arr.length - 1].value;
      const prev = arr[arr.length - 2].value;
      return last + (last - prev);
    },

    // --- Location mapping (internal only) ---
    mapZone(location) {
      if (/coast/i.test(location)) {
        return { code: '1A', label: '1A Grid (Coastal)' };
      } else {
        return { code: '9C', label: '9C Price (Inland Gauteng)' };
      }
    },

    // --- Helpers to compute APPROXIMATE recovery from FuelSA history ---
    // Returns { monthlyApproxCents, dailyApproxCents } OR nulls if insufficient data
    computeRecoveryApproxFromHistory(historyArr, filterFn) {
      const arr = historyArr.filter(filterFn);
      arr.sort((a, b) => new Date(a.date) - new Date(b.date));
      if (arr.length < 2) {
        return { monthlyApproxCents: null, dailyApproxCents: null };
      }
      const last = arr[arr.length - 1].value;       // latest pump price (c/l)
      const prev = arr[arr.length - 2].value;       // previous month pump price (c/l)
      const monthlyDelta = last - prev;             // c/l (sign shows up/down)
      const dailyApprox = monthlyDelta / this.REVIEW_DAYS; // c/l per review day
      return {
        monthlyApproxCents: monthlyDelta,
        dailyApproxCents: dailyApprox
      };
    },

    // --- Build the base list of cards: ULP 93, ULP 95, and D50ppm only ---
    buildFuelData(current, history) {
      const out = [];

      // Petrol (93 & 95)
      const petrolArr = Array.isArray(current.petrol) ? current.petrol : [];
      const histPetrol = Array.isArray(history.petrol) ? history.petrol : [];

      const selectPetrol = (octane) => {
        const candidates = petrolArr
          .filter(p => String(p.octane) === String(octane))
          .map(curr => {
            const zone = this.mapZone(curr.location);
            const filterFn = (h) => String(h.octane) === String(octane) && h.type === curr.type && h.location === curr.location;
            const pred = this.getPredicted(histPetrol, filterFn);

            // Approx recovery from pump prices (fallback)
            const approx = this.computeRecoveryApproxFromHistory(histPetrol, filterFn);

            return {
              key: `petrol-${octane}-${curr.type}-${curr.location}`,
              name: `ULP ${octane}`,
              zoneCode: zone.code,
              zoneLabel: zone.label,
              current: curr.value,
              predicted: pred,
              dailyRecoveryAvg: approx.dailyApproxCents,     // may be overwritten by real recovery later
              monthlyRecoveryAvg: approx.monthlyApproxCents, // may be overwritten by real recovery later
              recoverySource: approx.dailyApproxCents !== null ? 'approx' : null
            };
          });
        const chosen = candidates.find(c => c.zoneCode === '9C') || candidates[0];
        if (chosen) out.push(chosen);
      };
      selectPetrol('93');
      selectPetrol('95');

      // Diesel (keep only 50ppm)
      const dieselArr = Array.isArray(current.diesel) ? current.diesel : [];
      const histDiesel = Array.isArray(history.diesel) ? history.diesel : [];
      let addedDiesel = false;

      dieselArr.forEach(curr => {
        const ppm = Math.round(Number(curr.percentage) * 10000);
        if (ppm !== 50) return; // only 50ppm
        const zone = this.mapZone(curr.location);
        const filterFn = (h) => Math.round(Number(h.percentage) * 10000) === ppm && h.location === curr.location;
        const pred = this.getPredicted(histDiesel, filterFn);
        const approx = this.computeRecoveryApproxFromHistory(histDiesel, filterFn);

        out.push({
          key: `diesel-${ppm}-${curr.location}`,
          name: 'D50ppm',
          zoneCode: zone.code,
          zoneLabel: zone.label,
          current: curr.value,
          predicted: pred,
          dailyRecoveryAvg: approx.dailyApproxCents,
          monthlyRecoveryAvg: approx.monthlyApproxCents,
          recoverySource: approx.dailyApproxCents !== null ? 'approx' : null
        });
        addedDiesel = true;
      });

      // Fallback: if no 50ppm, show first available diesel as "D50ppm" label for UI continuity
      if (!addedDiesel && dieselArr.length) {
        const curr = dieselArr[0];
        const ppm = Math.round(Number(curr.percentage) * 10000);
        const zone = this.mapZone(curr.location);
        const filterFn = (h) => h.location === curr.location;
        const pred = this.getPredicted(histDiesel, filterFn);
        const approx = this.computeRecoveryApproxFromHistory(histDiesel, filterFn);

        out.push({
          key: `diesel-${ppm}-${curr.location}`,
          name: 'D50ppm',
          zoneCode: zone.code,
          zoneLabel: zone.label,
          current: curr.value,
          predicted: pred,
          dailyRecoveryAvg: approx.dailyApproxCents,
          monthlyRecoveryAvg: approx.monthlyApproxCents,
          recoverySource: approx.dailyApproxCents !== null ? 'approx' : null
        });
      }

      // Reduce to a single Diesel (prefer 9C)
      const dieselOnly = out.filter(item => String(item.key).startsWith('diesel-'));
      const dieselChosen = dieselOnly.find(i => i.zoneCode === '9C') || dieselOnly[0] || null;

      const finalList = out.filter(item => item.name === 'ULP 93' || item.name === 'ULP 95');
      if (dieselChosen) {
        finalList.push({ ...dieselChosen, name: 'D50ppm' });
      }

      this.fuelData = finalList;
    },

    // --- REAL recovery API integration (if provided) ---
    async fetchRecovery() {
      this.recoveryTried = true;
      this.recoveryAvailable = false;
      this.recoverySeries = [];
      this.recoveryByProduct = {};

      if (!this.RECOVERY_API_URL) return;

      try {
        const res = await fetch(this.RECOVERY_API_URL, { headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error(`Recovery API error: ${res.status}`);
        const raw = await res.json();

        const rows = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
        if (!rows.length) return;

        const series = [];
        for (const row of rows) {
          const dateStr = String(row.date || row.Date || row.day || '').slice(0, 10);
          const d = new Date(dateStr);
          if (isNaN(d.getTime())) continue;

          const productRaw = String(
            row.product ?? row.Product ?? row.fuel ?? row.Fuel ?? row.name ?? row.Name ?? ''
          ).toLowerCase();
          const octane = String(row.octane ?? row.Octane ?? '').trim();
          const isDiesel = /diesel|d50|50ppm|50 ppm/i.test(productRaw) ||
                           /diesel|d50|50ppm|50 ppm/i.test(String(row.type ?? '')) ||
                           /diesel|d50|50ppm|50 ppm/i.test(String(row.name ?? ''));

          let productKey = null;
          if (isDiesel) productKey = 'D50ppm';
          else if (/93/.test(productRaw) || octane === '93' || /ulp\s*93/i.test(productRaw)) productKey = 'ULP 93';
          else if (/95/.test(productRaw) || octane === '95' || /ulp\s*95/i.test(productRaw)) productKey = 'ULP 95';

          if (!productKey) continue;

          const rawVal =
            row.value ?? row.Value ??
            row.recovery ?? row.Recovery ??
            row.cents ?? row.Cents ??
            row.cpl ?? row.CPL ??
            row.cents_per_litre ?? row.centsPerLitre ??
            null;

          const num = Number(rawVal);
          if (!isFinite(num)) continue;

          series.push({
            date: d,
            productKey,
            valueCents: num
          });
        }

        if (!series.length) return;

        series.sort((a, b) => a.date - b.date);
        this.recoverySeries = series;

        const now = new Date();
        const month = now.getMonth();
        const year = now.getFullYear();

        const buckets = { 'ULP 93': [], 'ULP 95': [], 'D50ppm': [] };
        let latest = { 'ULP 93': null, 'ULP 95': null, 'D50ppm': null };

        for (const s of series) {
          if (!latest[s.productKey] || s.date > latest[s.productKey].date) {
            latest[s.productKey] = { date: s.date, valueCents: s.valueCents };
          }
          if (s.date.getMonth() === month && s.date.getFullYear() === year) {
            buckets[s.productKey].push(s.valueCents);
          }
        }

        const byProduct = {};
        ['ULP 93', 'ULP 95', 'D50ppm'].forEach(pk => {
          const latestCents = latest[pk]?.valueCents ?? null;
          let monthlyAvgCents = null;
          if (buckets[pk].length) {
            monthlyAvgCents = buckets[pk].reduce((a, b) => a + b, 0) / buckets[pk].length;
          } else {
            // fallback: last 30 days average
            const cutoff = new Date(now);
            cutoff.setDate(cutoff.getDate() - 30);
            const last30 = series.filter(s => s.productKey === pk && s.date >= cutoff).map(s => s.valueCents);
            if (last30.length) monthlyAvgCents = last30.reduce((a, b) => a + b, 0) / last30.length;
          }
          byProduct[pk] = {
            latestCents: latestCents !== null ? Number(latestCents) : null,
            monthlyAvgCents: monthlyAvgCents !== null ? Number(monthlyAvgCents) : null
          };
        });

        this.recoveryByProduct = byProduct;
        this.recoveryAvailable = true;
      } catch (err) {
        console.warn('Failed to load recovery data:', err?.message || err);
      }
    },

    applyRecoveryToCards() {
      if (!this.fuelData.length) return;

      if (this.recoveryAvailable) {
        // Overwrite approx with real recovery
        this.fuelData = this.fuelData.map(card => {
          const rec = this.recoveryByProduct[card.name];
          if (!rec) return card;
          return {
            ...card,
            dailyRecoveryAvg: rec.latestCents ?? null,
            monthlyRecoveryAvg: rec.monthlyAvgCents ?? null,
            recoverySource: 'real'
          };
        });
      } else {
        // Ensure approx values are clearly labeled
        this.fuelData = this.fuelData.map(card => ({
          ...card,
          recoverySource: (card.dailyRecoveryAvg !== null || card.monthlyRecoveryAvg !== null) ? 'approx' : null
        }));
      }
    },

    // --- Animations & UI controls ---
    animateCountUp() {
      this.fuelData.forEach((item, idx) => {
        // Note: if you want countup targets, add ids in template (e.g., :id="'price-'+idx'").
        const cu = new CountUp(`price-${idx}`, item.current / 100, { 
          decimalPlaces: 2, 
          duration: 2 
        });
        if (!cu.error) cu.start();
        if (item.predicted !== null) {
          const pu = new CountUp(`pred-${idx}`, item.predicted / 100, { 
            decimalPlaces: 2, 
            duration: 2 
          });
          if (!pu.error) pu.start();
        }
      });
    },

    startAutoScroll() {
      const c = this.$refs.scrollContainer;
      if (!c) return;
      this.pauseScroll();
      const max = c.scrollWidth - c.clientWidth;
      let pos = c.scrollLeft;
      this.scrollInterval = setInterval(() => {
        pos = pos >= max ? 0 : pos + 1;
        c.scrollLeft = pos;
      }, 50);
    },

    pauseScroll() {
      if (this.scrollInterval) clearInterval(this.scrollInterval);
      if (this.resumeTimeout) clearTimeout(this.resumeTimeout);
    },

    resumeScroll() {
      if (this.resumeTimeout) clearTimeout(this.resumeTimeout);
      this.resumeTimeout = setTimeout(() => this.startAutoScroll(), 1000);
    },

    checkScrollPosition() {
      const container = this.$refs.scrollContainer;
      if (!container) return;
      
      this.showLeftArrow = container.scrollLeft > 0;
      this.showRightArrow = container.scrollLeft < (container.scrollWidth - container.clientWidth - 1);
    },

    scrollLeft() {
      const container = this.$refs.scrollContainer;
      if (!container) return;
      this.pauseScroll();
      container.scrollBy({ left: -300, behavior: 'smooth' });
    },

    scrollRight() {
      const container = this.$refs.scrollContainer;
      if (!container) return;
      this.pauseScroll();
      container.scrollBy({ left: 300, behavior: 'smooth' });
    },

    // --- Market indicators ---
    async fetchMarketIndicators() {
      try {
        const brentRes = await fetch(`${this.EIA_URL}?api_key=${this.EIA_KEY}`);
        const brentJson = await brentRes.json();
        const series = brentJson?.responseData?.series ?? brentJson?.response?.data ?? brentJson?.series;
        if (Array.isArray(series) && series[0]?.data?.length) {
          const maybePair = series[0].data[0];
          let val = null;
          if (Array.isArray(maybePair)) val = Number(maybePair[1]);
          else if (typeof maybePair === 'object') val = Number(maybePair.value ?? maybePair[1]);
          if (isFinite(val)) this.brentPrice = val;
        }
      } catch (e) {
        console.warn('Brent fetch failed:', e?.message || e);
      }

      try {
        const fxRes = await fetch(this.FX_URL);
        const fxJson = await fxRes.json();
        const z = Number(fxJson?.rates?.ZAR);
        if (isFinite(z)) this.exchangeRate = z;
      } catch (e) {
        console.warn('FX fetch failed:', e?.message || e);
      }
    }
  },

  async mounted() {
    try {
      const today = new Date().toISOString().split('T')[0];
      const docRef = doc(db, 'fuelPrices', 'current');
      const docSnap = await getDoc(docRef);
      let currentData;
      if (docSnap.exists() && docSnap.data().date === today) {
        currentData = docSnap.data().prices;
      } else {
        currentData = await this.fetchCurrent();
        await setDoc(docRef, { date: today, prices: currentData });
      }

      let historyData;
      try {
        historyData = await this.fetchHistory(new Date().getFullYear());
      } catch (err) {
        if (String(err.message || '').includes('401')) {
          console.warn('401 on history, skipping predictions.');
          historyData = { petrol: [], diesel: [], paraffin: [] };
        } else throw err;
      }

      // Build base cards (ULP 93, ULP 95, D50ppm) with APPROX recovery prefilled
      this.buildFuelData(currentData, historyData);

      // Market indicators (Brent & USDZAR)
      await this.fetchMarketIndicators();

      // Try REAL recovery
      await this.fetchRecovery();
      this.applyRecoveryToCards();

      this.$nextTick(() => {
        this.animateCountUp();
        this.startAutoScroll();
        this.checkScrollPosition();
      });
    } catch (err) {
      console.error('FuelPrices error:', err);
    }
  }
};
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&display=swap');

.fuel-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem 1rem;
  background-color: #f5f7fa;
}

.fuel-header {
  font-family: "Montserrat", sans-serif;
  font-size: 2rem;
  font-weight: 900;
  color: #0e0e0e;
  margin-bottom: 1rem;
}

.market-indicators {
  display: flex;
  gap: 2rem;
  margin-bottom: 1rem;
  font-family: "Montserrat", sans-serif;
  justify-content: center;
  
  .indicator {
    font-size: 1rem;
    color: #333;
  }
}

.cards-mask {
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: 0 2rem;
  margin-top: 1rem;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    width: 3rem;
    height: 100%;
    pointer-events: none;
    z-index: 5;
  }
  
  &::before {
    left: 0;
    background: linear-gradient(to right, #f5f7fa 0%, #f5f7fa 50%, transparent 100%);
  }
  
  &::after {
    right: 0;
    background: linear-gradient(to left, #f5f7fa 0%, #f5f7fa 50%, transparent 100%);
  }
}

.scroll-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #72bf44, #5a9e35);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(114, 191, 68, 0.3);
  transition: all 0.3s ease;
  z-index: 10;
  border: 2px solid rgba(255, 255, 255, 0.2);
  
  &:hover {
    background: linear-gradient(135deg, #5a9e35, #4a8a2a);
    box-shadow: 0 6px 20px rgba(114, 191, 68, 0.4);
    transform: translateY(-50%) scale(1.15);
  }
  
  .v-icon {
    color: white;
    font-size: 28px;
    font-weight: bold;
  }
}

.left-arrow {
  left: 0.5rem;
}

.right-arrow {
  right: 0.5rem;
}

.cards-container {
  display: flex;
  gap: 1.5rem;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  justify-content: center;
}

.cards-container::-webkit-scrollbar {
  display: none;
}

.card-wrapper {
  flex: 0 0 auto;
}

.fuel-card {
  background: #fff;
  box-shadow: rgba(0, 0, 0, 0.45) 0px 25px 20px -20px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-8px) scale(1.05);
    box-shadow: rgba(0, 0, 0, 0.5) 0px 30px 25px -20px;
  }
}

.card-content {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  font-family: 'Montserrat', sans-serif;
  min-height: 120px;
}

.card-icon {
  background-color: rgba(114, 191, 68, 0.1);
  border-radius: 50%;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  transition: background-color 0.3s ease;
}

.fuel-card:hover .card-icon {
  background-color: rgba(114, 191, 68, 0.2);
}

.fuel-icon {
  font-size: 2rem;
  color: #72bf44;
  transition: transform 0.6s ease;
}

.fuel-card:hover .fuel-icon {
  transform: rotate(360deg);
}

.card-text {
  display: flex;
  flex-direction: column;
}

.card-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
}

.card-location {
  margin-top: 0.25rem;
  font-size: 0.95rem;
  color: #555;
}

.card-price {
  margin-top: 0.25rem;
  font-size: 1.6rem;
  font-weight: 700;
  color: #ba0000;
}

.card-predicted {
  margin-top: 0.5rem;
  font-size: 1rem;
  color: #333;
}

.recovery {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.recovery-item {
  font-size: 0.9rem;
  color: #333;
}

.price-direction {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 700;
  font-size: 1.2rem;
  width: 100%;
}

.direction-icon {
  font-size: 1.5rem;
}

.direction-up {
  color: #ba0000;
}

.direction-down {
  color: #4caf50;
}

.direction-stable {
  color: #666;
}

.price-direction .direction-up {
  background-color: #ba0000;
}

.price-direction .direction-down {
  background-color: #4caf50;
}

.price-direction .direction-stable {
  background-color: #666;
}

.direction-text {
  font-weight: 700;
}

.predicted-price {
  font-weight: 700;
  font-size: 1.6rem;
}

.price-labels {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.price-label {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.label-text {
  font-size: 0.8rem;
  font-weight: 600;
  color: #666;
}

.trend-indicator {
  width: 50px;
  height: 50px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.trend-icon {
  font-size: 28px;
  color: white;
}

.loading {
  margin-top: 2rem;
  font-style: italic;
  color: #666;
}
</style>
